import { useEffect, useMemo, useState } from "react";
import { addDays, format } from "date-fns";
import { id as localeId } from "date-fns/locale";
import type { BookingPrefill, BookingType } from "../../types/cafe";
import { activities } from "../../data/activities";

export interface BookingFormState {
  type: BookingType;
  outletId: string;
  activityId: string;
  guests: number;
  date: string;
  time: string;
  name: string;
  phone: string;
  email: string;
  notes: string;
}

export type BookingErrors = Partial<Record<keyof BookingFormState, string>>;
export type SubmitStatus = "idle" | "submitting" | "success";

export const TIME_SLOTS = ["08.00", "10.00", "13.00", "15.00", "17.00", "19.00"];

export const STEPS = ["Detail", "Jadwal", "Kontak"] as const;

function initialState(prefill: BookingPrefill): BookingFormState {
  const activity = activities.find((a) => a.id === prefill.activityId);
  return {
    type: prefill.type,
    outletId: prefill.outletId ?? activity?.outletIds[0] ?? "",
    activityId: prefill.activityId ?? "",
    guests: 2,
    date: "",
    time: "",
    name: "",
    phone: "",
    email: "",
    notes: "",
  };
}

export function isSlotFull(date: string, time: string): boolean {
  if (!date) return false;
  const seed = date.split("-").reduce((acc, n) => acc + Number(n), 0) + TIME_SLOTS.indexOf(time);
  return seed % 5 === 0;
}

export function useBookingForm(prefill: BookingPrefill, open: boolean) {
  const [form, setForm] = useState<BookingFormState>(() => initialState(prefill));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<BookingErrors>({});
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [code, setCode] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initialState(prefill));
      setStep(0);
      setErrors({});
      setStatus("idle");
    }
  }, [open, prefill]);

  const dates = useMemo(() => {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(today, i);
      return {
        value: format(d, "yyyy-MM-dd"),
        weekday: i === 0 ? "Hari ini" : format(d, "EEE", { locale: localeId }),
        day: format(d, "d"),
        month: format(d, "MMM", { locale: localeId }),
      };
    });
  }, []);

  const update = <K extends keyof BookingFormState>(key: K, value: BookingFormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "date") next.time = "";
      if (key === "activityId") {
        const act = activities.find((a) => a.id === value);
        if (act && !act.outletIds.includes(prev.outletId)) next.outletId = act.outletIds[0];
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (s: number): BookingErrors => {
    const e: BookingErrors = {};
    if (s === 0) {
      if (form.type === "activity" && !form.activityId) e.activityId = "Pilih aktivitas";
      if (!form.outletId) e.outletId = "Pilih outlet";
    }
    if (s === 1) {
      if (!form.date) e.date = "Pilih tanggal";
      if (!form.time) e.time = "Pilih jam";
    }
    if (s === 2) {
      if (form.name.trim().length < 2) e.name = "Masukkan nama kamu";
      if (!/^(\+62|0)8\d{7,11}$/.test(form.phone.replace(/[\s-]/g, "")))
        e.phone = "Gunakan nomor HP Indonesia, contoh 0812 3456 7890";
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        e.email = "Masukkan email yang valid";
    }
    return e;
  };

  const next = () => {
    const e = validate(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = () => {
    const e = validate(2);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setStatus("submitting");
    window.setTimeout(() => {
      setCode(`KLD-${Math.random().toString(36).slice(2, 7).toUpperCase()}`);
      setStatus("success");
    }, 900);
  };

  return { form, update, step, next, back, errors, status, submit, code, dates };
}
